import React, { useEffect, useState } from 'react';
import { Container } from '../../components/styleComponents';
import Loader from '../../components/Loader';
import { ISetor } from 'src/interfaces';
import api from 'src/service/api';
import { toast } from 'react-toastify';

import {
    Toolbar,
    TotalRegistros,
    FormContainer,
    FormRow,
    FormLabel,
    FormInput,
    CheckboxContainer,
    TableContainer,
    Table,
    TableRow,
    Pagination,
    SearchContainer,
} from './style';
import {
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaSave,
    FaTrash,
    FaEraser,
} from 'react-icons/fa';

const Setores: React.FC = () => {
    const [setores, setSetores] = useState<ISetor[]>([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [setorSelecionado, setSetorSelecionado] =
        useState<ISetor | null>(null);

    const [form, setForm] = useState<Partial<ISetor>>({
        setAtivo: true,
    });

    const modoInsercao = setorSelecionado === null;

    const [filtroNome, setFiltroNome] = useState('');

    const setoresFiltrados = setores.filter((setor) =>
    setor.setNome
        ?.toLowerCase()
        .includes(filtroNome.toLowerCase()),
);

    const updateForm = (
        field: keyof ISetor,
        value: string | number | boolean,
    ) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const carregarSetores = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            const response = await api.get('/setores');

            setSetores(response.data);
        } catch (error) {
            console.error(
                'Erro ao carregar setores',
                error,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarSetores();
    }, []);

    const limparFormulario = () => {
        setSetorSelecionado(null);

        setForm({
            setAtivo: true,
        });

        setFiltroNome('');
        setPage(1);
    };

    const selecionarSetor = (setor: ISetor) => {
        setSetorSelecionado(setor);

        setForm({
            setId: setor.setId,
            setNome: setor.setNome,
            setSigla: setor.setSigla,
            setTipo: setor.setTipo,
            setArea: setor.setArea,
            setIdSuperior: setor.setIdSuperior,
            setAtivo: setor.setAtivo,
            setManual: setor.setManual,
            setInativacao: formatarDataParaInput(
                setor.setInativacao,
            ),
        });
    };

    const salvar = async () => {

        if (!validarFormulario()) {
            return;
        }

        try {
            const token = sessionStorage.getItem('token');

            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;

            const payload = {
                setNome: form.setNome,
                setSigla: form.setSigla,
                setTipo: form.setTipo,
                setArea: form.setArea,
                setIdSuperior: form.setIdSuperior,
                setAtivo: form.setAtivo,
                setManual: form.setManual,
                setInativacao: formatarDataParaApi(
                    form.setInativacao as string,
                ),
            };

            if (modoInsercao) {
                await api.post('/setores/inclui', {
                    setId: form.setId,
                    ...payload,
                });
            } else {
                await api.put(`/setores/${form.setId}`, payload);
            }

            await carregarSetores();
            limparFormulario();
            toast.success('Setor salvo com sucesso.');
        } catch (error) {
            toast.error('Erro ao salvar setor.');
            console.error('Erro ao salvar setor', error);
        }
    };

    const excluir = async () => {
        if (!setorSelecionado) {
            return;
        }
        const confirmou = window.confirm(
            `Deseja excluir o setor "${setorSelecionado.setNome}"?`,
        );

        if (!confirmou) {
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            await api.delete('/setores', {
                data: {
                    ids: [setorSelecionado.setId],
                },
            });

            await carregarSetores();
            limparFormulario();
            setPage(1);
            toast.success('Setor excluído com sucesso.');
        } catch (error) {
            toast.error('Erro ao excluir setor.');
            console.error(
                'Erro ao excluir setor',
                error,
            );
        }
    };

    const totalPages = Math.ceil(
        setoresFiltrados.length / pageSize,
    );

    const setoresPaginados = setoresFiltrados.slice(
        (page - 1) * pageSize,
        page * pageSize,
    );

    if (loading) {
        return <Loader />;
    }

    const formatarDataParaInput = (
        data?: string | null,
    ): string => {
        if (!data) return '';

        return data.substring(0, 10);
    };

    const formatarDataParaApi = (
        data?: string,
    ): string | null => {
        if (!data) return null;

        return `${data} 00:00:00.000`;
    };

    const somenteNumeros = (valor: string): string => {
        return valor.replace(/\D/g, '');
    };

    const validarFormulario = (): boolean => {
        const campos: string[] = [];

        if (!form.setId) campos.push('Set_id');
        if (!form.setIdSuperior) campos.push('Set_id Superior');
        if (!form.setNome?.trim()) campos.push('Nome');
        if (!form.setSigla?.trim()) campos.push('Sigla');
        if (!form.setTipo?.trim()) campos.push('Tipo');
        if (!form.setInativacao) campos.push('Data de Inativação');
        if (form.setTipo && !['G', 'N', 'E'].includes(form.setTipo)
        ) {
            alert('O campo Tipo deve conter apenas G, N ou E.');
            return false;
        }

        if (campos.length > 0) {
            alert(
                `Os seguintes campos são obrigatórios:\n\n${campos.join('\n')}`,
            );
            return false;
        }

        return true;
    };

    return (
        <Container>
            <h2>Setores</h2>

            <Toolbar>
                <button
                    type="button"
                    onClick={salvar}
                >
                    <FaSave />
                    {modoInsercao ? 'Incluir' : 'Atualizar'}
                </button>

                <button
                    type="button"
                    onClick={limparFormulario}
                >
                    <FaEraser />
                    Limpar campos
                </button>

                <button
                    type="button"
                    disabled={!setorSelecionado}
                    onClick={excluir}
                >
                    <FaTrash />
                    Excluir
                </button>

                <TotalRegistros>
                    {modoInsercao ? 'Modo Inclusão' : `Modo Edição - Setor ${form.setId}`} - Total de registros:{' '}{setores.length}
                </TotalRegistros>
            </Toolbar>

            <FormContainer>
                <FormRow>
                    <FormLabel htmlFor="setId">
                        Set_id
                    </FormLabel>

                    <FormInput
                        id="setId"
                        width="80px"
                        type="text"
                        disabled={!modoInsercao}
                        value={form.setId ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);

                            updateForm(
                                'setId',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />
                    <FormLabel htmlFor="setNome">
                        Nome
                    </FormLabel>

                    <FormInput
                        id="setNome"
                        width="700px"
                        type="text"
                        value={form.setNome ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'setNome',
                                e.target.value,
                            )
                        }
                    />
                </FormRow>

                <FormRow>
                    <FormLabel htmlFor="setSigla">
                        Sigla
                    </FormLabel>

                    <FormInput
                        id="setSigla"
                        width="120px"
                        type="text"
                        value={form.setSigla ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'setSigla',
                                e.target.value,
                            )
                        }
                    />

                    <FormLabel
                        htmlFor="setTipo"
                        style={{
                            width: '80px',
                            minWidth: '80px',
                            marginLeft: '20px',
                        }}
                    >
                        Tipo
                    </FormLabel>

                    <FormInput
                        id="setTipo"
                        width="80px"
                        type="text"
                        value={form.setTipo ?? ''}
                        onChange={(e) => {
                            const valor = e.target.value
                                .toUpperCase()
                                .replace(/[^GNE]/g, '');

                            updateForm('setTipo', valor);
                        }}
                    />

                    <FormLabel
                        htmlFor="setIntivacao"
                        style={{
                            width: '140px',
                            minWidth: '140px',
                            marginLeft: '20px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Data de inativação
                    </FormLabel>

                    <FormInput
                        id="setIntivacao"
                        width="170px"
                        type="date"
                        value={
                            typeof form.setInativacao === 'string'
                                ? form.setInativacao
                                : ''
                        }
                        onChange={(e) =>
                            updateForm(
                                'setInativacao',
                                e.target.value,
                            )
                        }
                    />
                </FormRow>

                <FormRow>
                    <FormLabel htmlFor="setArea">
                        Área
                    </FormLabel>

                    <FormInput
                        id="setArea"
                        width="120px"
                        type="text"
                        value={form.setArea ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);

                            updateForm(
                                'setArea',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />

                    <FormLabel
                        htmlFor="setIdSuperior"
                        style={{
                        width: '120px',
                        minWidth: '120px',
                        marginLeft: '20px',
                        whiteSpace: 'nowrap',
                    }}
                    >
                        Set_id Superior
                    </FormLabel>

                    <FormInput
                        id="setIdSuperior"
                        width="80px"
                        type="text"
                        value={form.setIdSuperior ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);

                            updateForm(
                                'setIdSuperior',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />

                    <FormLabel
                        style={{
                            width: '60px',
                            minWidth: '60px',
                            marginLeft: '20px',
                        }}
                    >
                        Ativo
                    </FormLabel>

                    <CheckboxContainer>
                        <input
                            type="checkbox"
                            checked={form.setAtivo ?? false}
                            onChange={(e) =>
                                updateForm(
                                    'setAtivo',
                                    e.target.checked,
                                )
                            }
                        />
                    </CheckboxContainer>

                    <FormLabel
                        style={{
                            width: '60px',
                            minWidth: '60px',
                            marginLeft: '20px',
                        }}
                    >
                        Manual
                    </FormLabel>

                    <CheckboxContainer>
                        <input
                            type="checkbox"
                            checked={form.setManual ?? false}
                            onChange={(e) =>
                                updateForm(
                                    'setManual',
                                    e.target.checked,
                                )
                            }
                        />
                    </CheckboxContainer>
                </FormRow>
            </FormContainer>

            <SearchContainer>
                <label htmlFor="pesquisaNome">
                    Pesquisar Nome
                </label>

                <input
                    id="pesquisaNome"
                    type="text"
                    value={filtroNome}
                    onChange={(e) => {
                        setFiltroNome(e.target.value);
                        setPage(1);
                    }}
                />

                <span>
                    Encontrados: {setoresFiltrados.length}
                </span>
            </SearchContainer>

            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            <th>Set_id</th>
                            <th>Nome</th>
                            <th>Sigla</th>
                            <th>Tipo</th>
                            <th>Set_id Superior</th>
                            <th>Área</th>
                            <th>Ativo</th>
                            <th>Manual</th>
                        </tr>
                    </thead>

                    <tbody>
                        {setoresPaginados.map(
                            (setor) => (
                                <TableRow
                                    key={
                                        setor.setId
                                    }
                                    selected={
                                        setorSelecionado?.setId ===
                                        setor.setId
                                    }
                                    onClick={() =>
                                        selecionarSetor(
                                            setor,
                                        )
                                    }
                                >
                                    <td>
                                        {
                                            setor.setId
                                        }
                                    </td>
                                    <td>
                                        {
                                            setor.setNome
                                        }
                                    </td>
                                    <td>
                                        {
                                            setor.setSigla
                                        }
                                    </td>
                                    <td>
                                        {
                                            setor.setTipo
                                        }
                                    </td>
                                    <td>
                                        {
                                            setor.setIdSuperior
                                        }
                                    </td>
                                    <td>
                                        {
                                            setor.setArea
                                        }
                                    </td>
                                    <td>
                                        {setor.setAtivo
                                            ? 'Sim'
                                            : 'Não'}
                                    </td>
                                    <td>
                                        {setor.setManual
                                            ? 'Sim'
                                            : 'Não'}
                                    </td>
                                </TableRow>
                            ),
                        )}
                    </tbody>
                </Table>
            </TableContainer>

            <Pagination>
                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                >
                    <FaAngleDoubleLeft />
                    Primeira
                </button>

                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() =>
                        setPage((current) => current - 1)
                    }
                >
                    <FaAngleLeft />
                    Anterior
                </button>

                <span>Página {page} de {totalPages || 1}</span>

                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() =>
                        setPage((current) => current + 1)
                    }
                >
                    <FaAngleRight />
                    Próxima
                </button>

                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() => setPage(totalPages)}
                >
                    <FaAngleDoubleRight />
                    Última
                </button>
            </Pagination>
        </Container>
    );
};

export default Setores;
